import { Component, EventEmitter, Input, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { TblMaintenanceService } from './tbl-maintenance.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import * as _ from 'lodash-es';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-tbl-maintenance',
    templateUrl: './tbl-maintenance.component.html',
    styleUrls: ['./tbl-maintenance.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TblMaintenanceComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // 🚀 CRITICAL: Filter params input for dynamic data
  filterParams: any = {};

  // 🔴 GLOBAL SETTINGS AS @INPUT - DO NOT CREATE NEW SERVICES
  @Input('base-urls')
  set _base_urls(baseUrls: string){
    try{
      let urls: GtwElementsBaseUrlsModel = safeJsonInput<any>(baseUrls, null, 'tbl-maintenance.parse1');
      if(Object.keys(urls).length > 0){
        this.baseURLs = urls;
        this.isInitialized = false;
        this.tryInit();
      }
    }catch(e){
      console.log("Base URLs input not yet stable. Still waiting to get resolved.");
    }
  }
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  @Input('row-data')
  set _row_data(rowData: string){
    try{
      let data: any = safeJsonInput<any>(rowData, null, 'tbl-maintenance.parse2');
      if(Object.keys(data).length > 0){
        this.rowData = data;
        this.isInitialized = false;
        this.tryInit();
      }
    }catch(e){
      console.log("Row data input not yet stable. Still waiting to get resolved.");
    }
  }
  rowData: any = {};

  @Input('grid-data')
  set _grid_data(gridData: string){
    try{
      let data: any = safeJsonInput<any>(gridData, null, 'tbl-maintenance.parse3');
      if(data && data.length > 0){
        this.gridData = data;
        this.isInitialized = false;
        this.tryInit();
      }
    }catch(e){
      console.log("Grid data input not yet stable. Still waiting to get resolved.");
    }
  }
  gridData: any[] = [];

  @Input('filter-params')
  set _filter_params(filterParams: string){
    try{
      let params = safeJsonInput<any>(filterParams, null, 'tbl-maintenance.parse4');
      if(Object.keys(params).length > 0){
        this.filterParams = params;
        this.isInitialized = false;
        this.tryInit();
      }
    }catch(e){
      console.log("Filter params input not yet stable. Still waiting to get resolved.");
    }
  }

  @Input('header-data')
  set _header_data(headerData: string){
    try{
      let header = safeJsonInput<any>(headerData, null, 'tbl-maintenance.parse5');
      if(Object.keys(header).length > 0){
        this.globalService = header.globalParams;
        this.USER_SETTINGS = header.userSettingsData;
        this.SERVER_CONFIG = header.serverConfigData;
        this.GENERAL_CONFIG = header.generalConfigData;
        this.isInitialized = false;
        this.tryInit();
      }
    }catch(e){
      console.log("Input Not yet stable. Still waiting to get resolved.");
    }
  }

  @Input('mode')
  set _mode(mode: string){
    this.mode = mode;
    this.isInitialized = false;
    this.tryInit();
  }

  @Input('template-mode')
  set _templateMode(mode: string){
    if (mode) {
      this.mode = mode;
      this.isInitialized = false;
      this.tryInit();
    }
  }

  // Component properties
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  // Mode: 'insert' or 'delete'
  mode: string = '';

  // Delete mode properties - Following @sharedcomponent.mdc pattern
  showModal: boolean = false;
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to delete the selected table data?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };
  objectIdList: any[] = [];
  sendPopupData: any[] = [];

  // Insert mode properties
  crudLoading: boolean = false;
  loading: boolean = true;
  minimise: boolean = false;

  tblRowList: any[] = [];
  tblClList: any[] = [];
  srcColList: string[] = [];
  tarColList: string[] = [];
  matchColList: any[] = [];
  saveInsertObj: any[] = [];
  saveInsertObjClob: any[] = [];

  tblList: string[] = [];
  sTblList: string[] = ['Archive', 'Purge', 'Adhoc'];

  // Form fields
  rSrcTbl: string = '';
  rSTbl: string = '';
  rArchiveTblName: string = '';
  rWhereClause: string = '';
  showTbl: boolean = false;
  showWhereClauseField: boolean = false;

  // Day checkboxes - initialized as empty (no default selection)
  runSunYes: string = '';
  runMonYes: string = '';
  runTueYes: string = '';
  runWenYes: string = '';
  runThuYes: string = '';
  runFriYes: string = '';
  runSatYes: string = '';

  tblMaintenanceForm: UntypedFormGroup;

  private isInitialized = false;
  private isLoading = false;

  constructor(
    private fb: UntypedFormBuilder,
    private gtwSnackbarService: GtwSnackbarService,
    private tblMaintenanceService: TblMaintenanceService
  ) {
    this.tblMaintenanceForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.isInitialized = false;
    this.tryInit();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (
      !this.baseURLs ||
      Object.keys(this.baseURLs).length === 0 ||
      !this.GENERAL_CONFIG ||
      Object.keys(this.GENERAL_CONFIG).length === 0 ||
      !this.USER_SETTINGS ||
      Object.keys(this.USER_SETTINGS).length === 0
    ) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    // 🚀 CRITICAL: Ensure showModal stays false during initialization
    this.showModal = false;

    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0 &&
        this.USER_SETTINGS && Object.keys(this.USER_SETTINGS).length > 0) {
      this.determineMode();
      if (this.mode === 'insert') {
        this.initializeInsertMode();
      } else {
        this.initializeDeleteMode();
      }
    }
  }

  determineMode(): void {
    if (!this.mode || this.mode === '') {
      if (this.gridData && this.gridData.length > 0) {
        this.mode = 'delete';
        this.showModal = true;
      } else {
        this.mode = 'insert';
        this.showModal = false;
      }
    }
  }

  initializeInsertMode(): void {
    this.loading = true;
    this.fetchTableListData();
  }

  initializeDeleteMode(): void {
    this.prepareDeleteData();
  }

  // ============ DELETE MODE METHODS ============

  prepareDeleteData(): void {
    this.sendPopupData = [];
    this.objectIdList = [];

    if (this.gridData && this.gridData.length > 0) {
      for (let e = 0; e < this.gridData.length; e++) {
        const tmpJson: any = {};
        // tmpJson.ARC_TBL_NAME = this.gridData[e].data.ARC_TBL_NAME;
        tmpJson.DATE_COL_NAME = this.gridData[e].data.DATE_COL_NAME;
        // tmpJson.DATE_COL_TRANSFORM = this.gridData[e].data.DATE_COL_TRANSFORM;
        tmpJson.NUM_DAYS = this.gridData[e].data.NUM_DAYS;
        tmpJson.OWNER = this.gridData[e].data.OWNER;
        tmpJson.REC_TYPE = this.gridData[e].data.REC_TYPE;
        tmpJson.ROW_LIMIT = this.gridData[e].data.ROW_LIMIT;
        tmpJson.RUN_FRI_YN = this.gridData[e].data.RUN_FRI_YN;
        tmpJson.RUN_MON_YN = this.gridData[e].data.RUN_MON_YN;
        tmpJson.RUN_SAT_YN = this.gridData[e].data.RUN_SAT_YN;
        tmpJson.RUN_SUN_YN = this.gridData[e].data.RUN_SUN_YN;
        tmpJson.RUN_THU_YN = this.gridData[e].data.RUN_THU_YN;
        tmpJson.RUN_WED_YN = this.gridData[e].data.RUN_WED_YN;
        tmpJson.SOURCE_SYSTEM_FLAG = this.gridData[e].data.SOURCE_SYSTEM_FLAG;
        tmpJson.SRC_TBL_NAME = this.gridData[e].data.SRC_TBL_NAME;
        tmpJson.TBL_SEQUENCE_KEY = this.gridData[e].data.TBL_SEQUENCE_KEY;
        if (tmpJson.REC_TYPE === 'ADHOC') {
          tmpJson.WHERE_CLAUSE = this.gridData[e].data.WHERE_CLAUSE;
        }
        tmpJson.DELETE_YN = 'true';
        this.sendPopupData.push(tmpJson);
        this.objectIdList.push({ OBJECT_ID: this.gridData[e].data.OBJECT_ID });
      }
    }
  }

  onConfirm(): void {
    // Following @sharedcomponent.mdc pattern - delegate to confirmDelete
    this.confirmDelete();
  }

  onCancel(): void {
    // Following @sharedcomponent.mdc pattern - delegate to cancel
    this.cancel();
  }

  confirmDelete(): void {
    // Ensure delete data is prepared before deletion
    this.prepareDeleteData();

    // Validate that we have data to delete
    if (!this.sendPopupData || this.sendPopupData.length === 0) {
      this.gtwSnackbarService.openSnackBar('No data selected for deletion', 'gtw-snackbar--red');
      return;
    }

    // Following @sharedcomponent.mdc pattern - show loading immediately
    this.modalConfig.loading = true;
    this.crudLoading = true;

    // Dismiss modal immediately like AngularJS
    this.cancel();

    // 🚀 CRITICAL: Both jsonObj and jsonSettings should contain the same delete data
    // Payload structure: jsonSettings and jsonObj both contain records with DELETE_YN: "true"
    this.tblMaintenanceService.deleteTableMaintenance(
      this.baseURLs.api,
      this.sendPopupData,  // jsonObj - records to delete
      this.sendPopupData,   // jsonSettings - same records (as per payload structure)
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;
        this.modalConfig.loading = false;

        // 🚀 CRITICAL: Success message AFTER API call succeeds (like AngularJS)
        if (result.callSuccess !== '1') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage || 'Error deleting data', 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Data has been Deleted Successfully', 'gtw-snackbar--green');
          // Emit grid update event
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.crudLoading = false;
        this.modalConfig.loading = false;
        console.error('Error deleting table maintenance:', error);
        this.gtwSnackbarService.openSnackBar('Error deleting table maintenance', 'gtw-snackbar--red');
      }
    );
  }

  // ============ INSERT MODE METHODS ============

  fetchTableListData(): void {
    const ssoId = this.USER_SETTINGS?.user?.sso_id || '';

    this.tblMaintenanceService.loadTableList(
      this.baseURLs.api,
      ssoId,
      'GTWDBA01'
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1') {
          this.tblList = [];
          if (data.jsonObject && Array.isArray(data.jsonObject)) {
            for (let a1 = 0; a1 < data.jsonObject.length; a1++) {
              this.tblList.push(data.jsonObject[a1].TABLE_NAME);
            }
          }
          this.loading = false;
        } else {
          if (data.errorMessage === 'no access') {
            this.gtwSnackbarService.openSnackBar('Sorry you do not have access to do the requested action.', 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('An error has occurred. If you continue to experience this issue please visit the help page to contact support.', 'gtw-snackbar--red');
          }
          this.loading = false;
        }
      },
      (error: any) => {
        console.error('Error loading table list:', error);
        this.gtwSnackbarService.openSnackBar('Error loading table list', 'gtw-snackbar--red');
        this.loading = false;
      }
    );
  }

  changeTable(): void {
    if (this.rSrcTbl && this.rArchiveTblName) {
      this.fetchTableColumnData();
    }
  }

  fetchTableColumnData(): void {
    const ssoId = this.USER_SETTINGS?.user?.sso_id || '';

    this.tblMaintenanceService.loadTableColumnData(
      this.baseURLs.api,
      ssoId,
      this.rSrcTbl,
      this.rArchiveTblName,
      'GTWDBA01'
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1') {
          this.tblClList = data.jsonObject || [];
          this.srcColList = [];
          this.tarColList = [];
          this.matchColList = [];

          for (let d = 0; d < this.tblClList.length; d++) {
            this.srcColList.push(this.tblClList[d].SRC_COL_NAME);
            this.tarColList.push(this.tblClList[d].ARC_COL_NAME);

            if (this.tblClList[d].MATCH_YN === 'Y') {
              const tempJson: any = {};
              tempJson.SRC_COL_NAME = this.tblClList[d].SRC_COL_NAME;
              tempJson.ARC_COL_NAME = this.tblClList[d].ARC_COL_NAME;
              tempJson.MATCH_YN = this.tblClList[d].MATCH_YN;
              tempJson.COLUMN_ID = this.tblClList[d].COLUMN_ID;
              tempJson.INCLUDE_YN = (this.tblClList[d].INCLUDE_YN === 'TRUE');
              tempJson.src_dropdown = false;
              this.matchColList.push(tempJson);
            }

            if (this.tblClList[d].MATCH_YN === 'N' && !this.tblClList[d].SRC_COL_NAME) {
              const nottempJson: any = {};
              nottempJson.ARC_COL_NAME = this.tblClList[d].ARC_COL_NAME;
              nottempJson.COLUMN_ID = this.tblClList[d].COLUMN_ID;
              nottempJson.src_dropdown = true;
              nottempJson.INCLUDE_YN = (this.tblClList[d].INCLUDE_YN === 'TRUE');
              this.matchColList.push(nottempJson);
            }
          }
        } else {
          if (data.errorMessage === 'no access') {
            this.gtwSnackbarService.openSnackBar('Sorry you do not have access to do the requested action.', 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('An error has occurred. If you continue to experience this issue please visit the help page to contact support.', 'gtw-snackbar--red');
          }
        }
      },
      (error: any) => {
        console.error('Error loading table column data:', error);
        this.gtwSnackbarService.openSnackBar('Error loading table column data', 'gtw-snackbar--red');
      }
    );
  }

  changesName(): void {
    if (this.rSTbl === 'Archive') {
      this.showTbl = true;
      this.showWhereClauseField = false;
    } else if (this.rSTbl === 'Adhoc') {
      this.showWhereClauseField = true;
      this.showTbl = false;
    } else {
      this.showTbl = false;
      this.showWhereClauseField = false;
      this.rArchiveTblName = '';
      this.tblClList = [];
      this.srcColList = [];
      this.tarColList = [];
      this.matchColList = [];
    }
  }

  addTableRow(): void {
    const tjson: any = {};
    tjson.SRC_COL_NAME = '';
    tjson.ARC_COL_NAME = '';
    tjson.MATCH_YN = '';
    tjson.COLUMN_ID = '';
    tjson.INCLUDE_YN = true;
    tjson.src_dropdown = true;
    this.matchColList.push(tjson);
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.crudLoading = true;
    this.saveInsertObj = [];
    this.saveInsertObjClob = [];

    const dummyJsonSaveObj: any = {};
    dummyJsonSaveObj.ARC_TBL_NAME = this.rArchiveTblName;
    dummyJsonSaveObj.DATE_COL_NAME = 'CREATED_ON';
    dummyJsonSaveObj.DATE_COL_TRANSFORM = '';
    dummyJsonSaveObj.NUM_DAYS = 180;
    dummyJsonSaveObj.OWNER = 'GTWDBA01';
    dummyJsonSaveObj.REC_TYPE = this.rSTbl;
    dummyJsonSaveObj.ROW_LIMIT = 200000;
    // 🚀 CRITICAL: Handle empty values - default to 'N' if not explicitly set to 'Yes'
    dummyJsonSaveObj.RUN_FRI_YN = (this.runFriYes === 'Yes') ? 'Y' : 'N';
    dummyJsonSaveObj.RUN_MON_YN = (this.runMonYes === 'Yes') ? 'Y' : 'N';
    dummyJsonSaveObj.RUN_SAT_YN = (this.runSatYes === 'Yes') ? 'Y' : 'N';
    dummyJsonSaveObj.RUN_SUN_YN = (this.runSunYes === 'Yes') ? 'Y' : 'N';
    dummyJsonSaveObj.RUN_THU_YN = (this.runThuYes === 'Yes') ? 'Y' : 'N';
    dummyJsonSaveObj.RUN_TUE_YN = (this.runTueYes === 'Yes') ? 'Y' : 'N';
    dummyJsonSaveObj.RUN_WED_YN = (this.runWenYes === 'Yes') ? 'Y' : 'N';
    dummyJsonSaveObj.SOURCE_SYSTEM_FLAG = 'D';
    dummyJsonSaveObj.SRC_TBL_NAME = this.rSrcTbl;
    dummyJsonSaveObj.TBL_SEQUENCE_KEY = '';
    dummyJsonSaveObj.DELETE_YN = 'false';

    if (this.showWhereClauseField) {
      dummyJsonSaveObj.WHERE_CLAUSE = this.rWhereClause;
      dummyJsonSaveObj.DATE_COL_NAME = null;
      dummyJsonSaveObj.DATE_COL_TRANSFORM = null;
      dummyJsonSaveObj.ROW_LIMIT = null;
      dummyJsonSaveObj.NUM_DAYS = null;
    }

    this.saveInsertObj.push(dummyJsonSaveObj);
    this.saveInsertObjClob = this.matchColList;

    this.tblMaintenanceService.saveTableMaintenance(
      this.baseURLs.api,
      this.saveInsertObj,
      this.saveInsertObjClob,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;

        // 🚀 CRITICAL: Success message AFTER API call succeeds (like AngularJS)
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Data has been saved', 'gtw-snackbar--green');
          // Emit grid update event - following @sharedcomponent.mdc pattern
          this.requestGridUpdate.emit('refresh');
          // Dismiss modal immediately like AngularJS
          this.cancel();
        }
      },
      (error: any) => {
        this.crudLoading = false;
        console.error('Error saving table maintenance:', error);
        this.gtwSnackbarService.openSnackBar('Error saving table maintenance', 'gtw-snackbar--red');
      }
    );
  }

  // ============ COMMON METHODS ============

  cancel(): void {
    // Following @sharedcomponent.mdc pattern - fir163jsync cancel pattern
    this.showModal = false;
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    if (document.querySelector('gtw-tbl-maintenance-ce')) {
      document.querySelector('gtw-tbl-maintenance-ce')?.dispatchEvent(updateEvent);
    }
  }
}

